import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { isSupabaseConfigured } from '../../../lib/supabase';
import { fetchRestaurantProfile } from '../../../services/menuDataService';
import { supabase } from '../../../lib/supabase';

const SupabaseHealthPanel = ({ onToast }) => {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  const projectHost = (() => {
    try {
      const url = import.meta.env?.VITE_SUPABASE_URL;
      return url ? new URL(url).hostname : '—';
    } catch {
      return '—';
    }
  })();

  const runReadTest = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const profile = await fetchRestaurantProfile();
      setStatus({
        ok: true,
        message: `Lecture OK — profil: ${profile?.name || '(sans nom)'}`,
      });
    } catch (e) {
      setStatus({ ok: false, message: e?.message || 'Échec de lecture' });
      onToast?.(e?.message || 'Échec de lecture Supabase', 'error');
    } finally {
      setTesting(false);
    }
  };

  const runWriteTest = async () => {
    if (!supabase) {
      setStatus({ ok: false, message: 'Supabase non configuré' });
      onToast?.('Supabase non configuré', 'error');
      return;
    }
    setTesting(true);
    setStatus(null);
    const testId = 9999999990000 + Math.floor(Math.random() * 999999);
    try {
      const { error: insertError } = await supabase.from('menu_dishes').insert({
        id: testId,
        name: '__health_check__',
        price: 0,
        category: 'mains',
        hidden: true,
      });
      if (insertError) throw insertError;

      const { error: deleteError } = await supabase
        .from('menu_dishes')
        .delete()
        .eq('id', testId);
      if (deleteError) throw deleteError;

      setStatus({
        ok: true,
        message: 'Écriture OK — INSERT et DELETE autorisés (RLS)',
      });
    } catch (e) {
      const msg =
        e?.message?.includes('row-level security') || e?.code === '42501'
          ? 'RLS bloque INSERT — exécutez la migration 20260601120000_fix_anon_insert_policies.sql dans Supabase'
          : e?.message || 'Échec du test d\'écriture';
      setStatus({ ok: false, message: msg });
      onToast?.(msg, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex items-center gap-2 mb-3">
        <Icon
          name={isSupabaseConfigured() ? 'Database' : 'AlertTriangle'}
          size={20}
          color={isSupabaseConfigured() ? 'var(--color-primary)' : 'var(--color-warning)'}
        />
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
          Connexion Supabase
        </h3>
      </div>
      <dl className="text-sm space-y-1 mb-4 font-caption text-muted-foreground">
        <div className="flex gap-2">
          <dt className="font-medium text-foreground">Statut:</dt>
          <dd>{isSupabaseConfigured() ? 'Configuré' : 'Non configuré (vérifiez .env)'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-medium text-foreground">Projet:</dt>
          <dd className="font-mono text-xs break-all">{projectHost}</dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={runReadTest}
          disabled={testing || !isSupabaseConfigured()}
          iconName="Eye"
          iconPosition="left"
        >
          Tester lecture
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={runWriteTest}
          disabled={testing || !isSupabaseConfigured()}
          iconName="PenLine"
          iconPosition="left"
        >
          Tester écriture (RLS)
        </Button>
      </div>
      {status && (
        <p
          className={`mt-3 text-sm ${status.ok ? 'text-green-700' : 'text-red-600'}`}
          role="status"
        >
          {status.message}
        </p>
      )}
    </div>
  );
};

export default SupabaseHealthPanel;
